import type { SettingsStorage } from '@/core/settings';

/**
 * The angler's record book.
 *
 * In localStorage rather than the save file, deliberately: a record is
 * something the *player* achieved, not something a playthrough owns. Starting a
 * new farm should not wipe the 2m sturgeon, and the book is small, synchronous
 * to read, and wanted on the very first frame a fish is landed.
 */

const STORAGE_KEY = 'heartland:records';
// Bumped when the stored shape changed from centimetres to inches; a v1 book
// would silently read as absurdly small fish.
const VERSION = 2;

export interface CatchRecord {
  /** Longest of this species ever landed, in inches. */
  bestIn: number;
  /** Weight of that fish, so the book does not have to recompute it. */
  bestLb: number;
  /** How many have been landed in total. */
  caught: number;
  /** Wall-clock time of the best one, for a future record screen. */
  bestAt: number;
}

export interface RecordEntry extends CatchRecord {
  readonly id: string;
}

interface StoredBook {
  version: number;
  entries: Record<string, CatchRecord>;
}

export type CatchOutcome = 'first' | 'record' | 'ordinary';

export class RecordBook {
  private readonly entries = new Map<string, CatchRecord>();

  constructor(private readonly storage: SettingsStorage | null = defaultStorage()) {
    this.load();
  }

  get(id: string): CatchRecord | undefined {
    return this.entries.get(id);
  }

  get speciesCaught(): number {
    return this.entries.size;
  }

  all(): RecordEntry[] {
    return Array.from(this.entries, ([id, record]) => ({ id, ...record }));
  }

  /**
   * Log a catch and say what it was worth.
   *
   * `first` is distinct from `record` on purpose: calling the very first of a
   * species "a new record" is technically true and emotionally meaningless.
   */
  record(id: string, inches: number, lb: number, at: number): CatchOutcome {
    const existing = this.entries.get(id);

    if (!existing) {
      this.entries.set(id, { bestIn: inches, bestLb: lb, caught: 1, bestAt: at });
      this.save();
      return 'first';
    }

    existing.caught++;
    const beaten = inches > existing.bestIn;
    if (beaten) {
      existing.bestIn = inches;
      existing.bestLb = lb;
      existing.bestAt = at;
    }
    this.save();
    return beaten ? 'record' : 'ordinary';
  }

  clear(): void {
    this.entries.clear();
    try {
      this.storage?.removeItem(STORAGE_KEY);
    } catch (error) {
      console.warn('Could not clear the record book.', error);
    }
  }

  private load(): void {
    if (!this.storage) return;
    try {
      const raw = this.storage.getItem(STORAGE_KEY);
      if (!raw) return;

      const parsed = JSON.parse(raw) as Partial<StoredBook>;
      if (parsed.version !== VERSION) return;
      const entries = parsed.entries;
      if (typeof entries !== 'object' || entries === null) return;

      // Validated per entry: one corrupt species must not cost the player the
      // rest of the book.
      for (const [id, value] of Object.entries(entries)) {
        const record = value as Partial<CatchRecord>;
        if (typeof record?.bestIn !== 'number' || !Number.isFinite(record.bestIn)) continue;
        if (record.bestIn <= 0) continue;

        this.entries.set(id, {
          bestIn: record.bestIn,
          bestLb:
            typeof record.bestLb === 'number' && Number.isFinite(record.bestLb)
              ? record.bestLb
              : 0,
          caught:
            typeof record.caught === 'number' && Number.isFinite(record.caught)
              ? Math.max(1, Math.floor(record.caught))
              : 1,
          bestAt: typeof record.bestAt === 'number' ? record.bestAt : 0,
        });
      }
    } catch (error) {
      console.warn('Could not read the record book; starting a fresh one.', error);
    }
  }

  private save(): void {
    if (!this.storage) return;
    try {
      const book: StoredBook = {
        version: VERSION,
        entries: Object.fromEntries(this.entries),
      };
      this.storage.setItem(STORAGE_KEY, JSON.stringify(book));
    } catch (error) {
      // Private browsing and full quotas both throw. A record that does not
      // persist is much better than a crash on landing a fish.
      console.warn('Could not save the record book.', error);
    }
  }
}

function defaultStorage(): SettingsStorage | null {
  try {
    return typeof localStorage === 'undefined' ? null : localStorage;
  } catch {
    return null;
  }
}
